/**
 * useRitualWhoopCorrelation - Correlates ritual completion with WHOOP biometric outcomes
 * Shows how rituals impact recovery, HRV, and sleep performance
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWhoopDataCached } from './useWhoopDataCached';
import { format, subDays, parseISO, isSameDay } from 'date-fns';

export interface RitualWhoopCorrelation {
  // Days with full rituals vs without
  daysWithBothRituals: number;
  daysWithMorningOnly: number;
  daysWithEveningOnly: number;
  daysWithNoRituals: number;
  
  // Recovery correlation
  avgRecoveryWithRituals: number | null;
  avgRecoveryWithoutRituals: number | null;
  recoveryImprovementPercent: number | null;
  
  // HRV correlation
  avgHrvWithRituals: number | null;
  avgHrvWithoutRituals: number | null;
  hrvImprovementPercent: number | null;
  
  // Sleep correlation (for evening ritual)
  avgSleepWithEveningRitual: number | null;
  avgSleepWithoutEveningRitual: number | null;
  sleepImprovementPercent: number | null;
  
  // Overall insight message
  insightMessage: string;
  hasSignificantData: boolean;
}

interface RitualLog {
  date: string;
  routine_type: 'morning' | 'evening';
  completed: boolean;
}

export function useRitualWhoopCorrelation(days: number = 30) {
  const { data: whoopData, isConnected: hasWhoop, isLoading: whoopLoading } = useWhoopDataCached(days);

  return useQuery<RitualWhoopCorrelation | null>({
    queryKey: ['ritual-whoop-correlation', days],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Fetch ritual logs for the period
      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
      
      const { data: ritualLogs, error } = await supabase
        .from('daily_ritual_logs')
        .select('log_date, routine_type')
        .eq('user_id', user.id)
        .gte('log_date', startDate);

      if (error) {
        console.error('Error fetching ritual logs:', error);
        return null;
      }

      // Group rituals by date
      const ritualsByDate: Record<string, { morning: boolean; evening: boolean }> = {};
      
      (ritualLogs || []).forEach((log: any) => {
        const date = log.log_date;
        if (!ritualsByDate[date]) {
          ritualsByDate[date] = { morning: false, evening: false };
        }
        if (log.routine_type === 'morning') {
          ritualsByDate[date].morning = true;
        } else if (log.routine_type === 'evening') {
          ritualsByDate[date].evening = true;
        }
      });

      // Initialize counters
      let daysWithBothRituals = 0;
      let daysWithMorningOnly = 0;
      let daysWithEveningOnly = 0;
      let daysWithNoRituals = 0;

      // Recovery correlations
      const recoveryWithRituals: number[] = [];
      const recoveryWithoutRituals: number[] = [];
      
      // HRV correlations
      const hrvWithRituals: number[] = [];
      const hrvWithoutRituals: number[] = [];
      
      // Sleep correlations (evening ritual impacts next day)
      const sleepWithEveningRitual: number[] = [];
      const sleepWithoutEveningRitual: number[] = [];

      // Process each day's WHOOP data
      whoopData?.forEach((whoop) => {
        const date = whoop.date;
        const rituals = ritualsByDate[date];
        
        const hasMorning = rituals?.morning || false;
        const hasEvening = rituals?.evening || false;
        const hasBoth = hasMorning && hasEvening;
        const hasAny = hasMorning || hasEvening;

        // Count ritual days
        if (hasBoth) daysWithBothRituals++;
        else if (hasMorning) daysWithMorningOnly++;
        else if (hasEvening) daysWithEveningOnly++;
        else daysWithNoRituals++;

        // Recovery correlation (morning ritual -> same day recovery)
        if (whoop.recovery_score != null) {
          if (hasAny) {
            recoveryWithRituals.push(whoop.recovery_score);
          } else {
            recoveryWithoutRituals.push(whoop.recovery_score);
          }
        }

        // HRV correlation
        if (whoop.hrv_score != null) {
          if (hasAny) {
            hrvWithRituals.push(whoop.hrv_score);
          } else {
            hrvWithoutRituals.push(whoop.hrv_score);
          }
        }

        // Sleep correlation (evening ritual -> next day's sleep score)
        // Look at previous day's evening ritual
        const prevDate = format(subDays(parseISO(date), 1), 'yyyy-MM-dd');
        const prevRituals = ritualsByDate[prevDate];
        
        if (whoop.sleep_performance != null) {
          if (prevRituals?.evening) {
            sleepWithEveningRitual.push(whoop.sleep_performance);
          } else {
            sleepWithoutEveningRitual.push(whoop.sleep_performance);
          }
        }
      });

      // Calculate averages
      const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
      const improvement = (with_: number | null, without_: number | null) => {
        if (with_ == null || without_ == null || without_ === 0) return null;
        return Math.round(((with_ - without_) / without_) * 100);
      };

      const avgRecoveryWithRituals = avg(recoveryWithRituals);
      const avgRecoveryWithoutRituals = avg(recoveryWithoutRituals);
      const avgHrvWithRituals = avg(hrvWithRituals);
      const avgHrvWithoutRituals = avg(hrvWithoutRituals);
      const avgSleepWithEveningRitual = avg(sleepWithEveningRitual);
      const avgSleepWithoutEveningRitual = avg(sleepWithoutEveningRitual);

      const recoveryImprovementPercent = improvement(avgRecoveryWithRituals, avgRecoveryWithoutRituals);
      const hrvImprovementPercent = improvement(avgHrvWithRituals, avgHrvWithoutRituals);
      const sleepImprovementPercent = improvement(avgSleepWithEveningRitual, avgSleepWithoutEveningRitual);

      // Generate insight message
      const hasSignificantData = 
        recoveryWithRituals.length >= 5 && recoveryWithoutRituals.length >= 3;

      let insightMessage = "";
      if (!hasSignificantData) {
        insightMessage = "Keep tracking to see how rituals affect your recovery.";
      } else if (recoveryImprovementPercent && recoveryImprovementPercent > 5) {
        insightMessage = `Your recovery is ${recoveryImprovementPercent}% higher on days you complete rituals.`;
      } else if (sleepImprovementPercent && sleepImprovementPercent > 5) {
        insightMessage = `Your sleep performance is ${sleepImprovementPercent}% better after evening rituals.`;
      } else {
        insightMessage = "Your rituals are contributing to consistent wellbeing patterns.";
      }

      return {
        daysWithBothRituals,
        daysWithMorningOnly,
        daysWithEveningOnly,
        daysWithNoRituals,
        avgRecoveryWithRituals,
        avgRecoveryWithoutRituals,
        recoveryImprovementPercent,
        avgHrvWithRituals,
        avgHrvWithoutRituals,
        hrvImprovementPercent,
        avgSleepWithEveningRitual,
        avgSleepWithoutEveningRitual,
        sleepImprovementPercent,
        insightMessage,
        hasSignificantData,
      };
    },
    enabled: hasWhoop && whoopData && whoopData.length > 0,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

export default useRitualWhoopCorrelation;
